import React from 'react';

import { AccordionDetails, Checkbox, FormControlLabel, FormGroup, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import OverrideControls from './OverrideControls';
import { TeamsAdminSettings } from './TeamsAdminSettings';
import UnauthorizedUserAccessSetting from './UnauthUserAccessSetting';
import UserAccountCreationSetting from './UserAccountCreationSetting';
import ZoomAdminSettings from './ZoomAdminSettings';
import Select from '../../../components/forms/Select';
import { ClassLevelOverrideOptions } from '../../../types/adminConfig';
import AdminSettingsContainer from '../AdminSettingsContainer';
import Accordion from '../components/Accordion';
import SaveButton from '../components/SaveButton';
import { StyledFormGroupContainer } from '../styles';
import StyledFormTitle from '../styles/form-title';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const StyledHelperText = styled(Typography)`
    color: ${(props) => props.theme.colors.text.secondary};
`;

const StyledSelect = styled(Select)`
    width: 400px;
`;

const StyledContainer = styled.div`
    margin-bottom: 20px;
`;

function isValidZoomAccountID(id: string): boolean {
    return /^[a-zA-Z0-9-]{10,}$/.test(id);
}

const validateAccountIds = (accountIds?: string[]) => {
    if (!accountIds || accountIds.length === 0) {
        return true;
    }

    return !accountIds.find((accountId) => !isValidZoomAccountID(accountId));
};

const AuthSettings = () => {
    const { adminSettings, setAdminSettings, isSavingSettingsData, isSavingDisabled, handleSaveSettings, school } =
        useContainer(AdminSettingsContainer);
    const { authSettings, featureSettings } = adminSettings;

    const isManagedZoom = school?.isManagedZoom;
    const isTeams = school?.isTeams;
    const isRegularZoom = !isManagedZoom && !isTeams;

    const { t } = useTranslation('admin_settings');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id || event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.options[event.target.name] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOverrides = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.overrides[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const renderNonDemo = () => {
        if (adminSettings.featureSettings.options.salesTrainingMode) return null;

        const showUserAccountCreationSetting = school?.isTeams || school?.isManagedZoom;

        return (
            <>
                {showUserAccountCreationSetting && <UserAccountCreationSetting />}

                <Accordion title={t('auth.joining-class-session')}>
                    <AccordionDetails>
                        <UnauthorizedUserAccessSetting />
                    </AccordionDetails>
                </Accordion>
            </>
        );
    };

    return (
        <>
            <StyledFormTitle>{t('auth.title')}</StyledFormTitle>

            <StyledContainer>
                {renderNonDemo()}

                <Accordion title={t('auth.enrolling-in-a-class')}>
                    <StyledFormGroupContainer>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={featureSettings.options.rosterSelfEnrollment}
                                        onChange={handleUpdateOptionsCheckboxChange}
                                        id="rosterSelfEnrollment"
                                        name="rosterSelfEnrollment"
                                    />
                                }
                                label={t('auth.selfEnrollment')}
                            />
                            <OverrideControls
                                overrideName="rosterSelfEnrollment"
                                overrideValue={
                                    featureSettings.overrides?.rosterSelfEnrollment ?? ClassLevelOverrideOptions.NoOne
                                }
                                handleUpdateOverrides={handleUpdateOverrides}
                                labelText={t('whoCanOverride')}
                                tooltipText={t('whoCanOverrideTooltip')}
                            />
                        </FormGroup>
                    </StyledFormGroupContainer>
                </Accordion>

                {isRegularZoom && (
                    <Accordion title={t('auth.zoom-authentication')}>
                        <ZoomAdminSettings />
                    </Accordion>
                )}

                {isTeams && (
                    <Accordion title={t('auth.microsoft-authentication')}>
                        <TeamsAdminSettings />
                    </Accordion>
                )}

                {(isRegularZoom || !isTeams) && (
                    <Accordion title={t('auth.advanced-settings')}>
                        {isRegularZoom && (
                            <StyledFormGroupContainer>
                                <AdminControlCheckbox
                                    control={
                                        <Checkbox
                                            checked={authSettings.forcedAuthentication}
                                            onChange={handleCheckboxChange}
                                            id="forcedAuthentication"
                                            name="forcedAuthentication"
                                        />
                                    }
                                    label={t('auth.forcedAuthenticationControl')}
                                />
                                <StyledHelperText variant="body2">{t('auth.helperTextLine1')}</StyledHelperText>
                            </StyledFormGroupContainer>
                        )}

                        {!isTeams && (
                            <StyledFormGroupContainer>
                                <FormGroup>
                                    <StyledSelect
                                        name="autoVerify"
                                        id="autoVerify"
                                        value={authSettings.autoVerify}
                                        label={t('auth.autoVerify')}
                                        labelId="select-auto-verify"
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={'never'}>{t('auth.autoVerifyNever')}</MenuItem>
                                        <MenuItem value={'optional'}>{t('auth.autoVerifyOptional')}</MenuItem>
                                        <MenuItem value={'always'}>{t('auth.autoVerifyAlways')}</MenuItem>
                                    </StyledSelect>
                                </FormGroup>
                            </StyledFormGroupContainer>
                        )}
                    </Accordion>
                )}
            </StyledContainer>

            <StyledFormGroupContainer>
                <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />
            </StyledFormGroupContainer>
        </>
    );
};

export default AuthSettings;
